import React, { useEffect } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import LabelName from 'components/UI/LabelName/LabelName';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { reminderChannels, reminderOptions, reminderCountOptions, reminderCustomOptions } from 'constants/dropdownOptions';

function EventReminders({ watch, control, isGlobal, errors, setValue, isRequired = false, trigger }) {
  const { fields, insert, remove } = useFieldArray({
    control,
    name: 'reminder',
    rules: {
      validate: (value) => {
        // Block submit if any reminder row has no channel selected
        if (value?.some((r) => !r?.reminder_channel_type)) {
          return 'Please select a reminder channel';
        }

        // Get current attendees to check if reminder is required
        const contacts = watch('contacts') || [];
        const firmUsers = watch('firm_users') || [];
        const groups = watch('groups') || [];

        const hasAttendeesWithReminder =
          contacts.some((c) => c?.send_reminder === true) ||
          firmUsers.some((u) => u?.send_reminder === true) ||
          groups.some((g) => g?.send_reminder === true);

        if (hasAttendeesWithReminder && (!value || value.length === 0)) {
          return 'At least one reminder is required';
        }
        return true;
      },
    },
  });

  // Re-validate when attendees, not_linked_to_case, or firm_wide change
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        name?.startsWith('contacts') ||
        name?.startsWith('firm_users') ||
        name?.startsWith('groups') ||
        name === 'not_linked_to_case' ||
        name === 'firm_wide'
      ) {
        if (trigger) {
          trigger('reminder');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  function onAddReminder() {
    insert(fields.length, { value: '30#minutes#', reminder_channel_type: 'inapp_notification' });
  }

  function onDeleteReminder(index) {
    remove(index);
  }

  const notLinkedToCase = watch('not_linked_to_case');
  const firmWide = watch('firm_wide');
  const hideSecureMessage = isGlobal && (notLinkedToCase || firmWide);

  return (
    <div className="d-flex flex-wrap w-100 pb-2">
      <div className="col-md-4 col-12 mt-2">
        <LabelName required={isRequired}>Reminders</LabelName>
      </div>
      <div className="col-md-8 col-12 mt-2">
        {fields?.length > 0 &&
          fields.map((data, index) => {
            let reminderValue = watch(`reminder.${index}.value`);
            return (
              <div className="d-flex input-shadow align-items-center justify-content-center p-2 mb-2 bg-grey mt-2" key={data.id}>
                <div className="w-100">
                  <div>
                    <Controller
                      name={`reminder.${index}.reminder_channel_type`}
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          placeholder="Channel"
                          name={field.name}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.value);
                            trigger?.('reminder');
                          }}
                          options={reminderChannels?.filter((item) => !(item?.value === 'secure_message' && hideSecureMessage)) || []}
                          className="w-100"
                          filter
                        />
                      )}
                    />
                  </div>
                  <div className="mt-2">
                    <Controller
                      name={`reminder.${index}.value`}
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          placeholder="Add"
                          name={field.name}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.value);
                            if (e.value === 'custom') {
                              setValue(`reminder.${index}.customValue`, 1);
                              setValue(`reminder.${index}.minutes`, 'minutes');
                              setValue(`reminder.${index}.type`, 'minutes');
                            } else {
                              setValue(`reminder.${index}.customValue`, null);
                              setValue(`reminder.${index}.minutes`, null);
                              setValue(`reminder.${index}.type`, null);
                            }
                          }}
                          options={reminderOptions?.map((item) => ({
                            ...item,
                            label: item?.value === 'custom' ? item.label : `${item?.label} before event start time`,
                          }))}
                          className="w-100"
                          filter
                        />
                      )}
                    />
                  </div>

                  {reminderValue === 'custom' && (
                    <div className="d-flex flex-column">
                      <div className="d-flex mt-2 gap-2">
                        <Controller
                          name={`reminder.${index}.customValue`}
                          control={control}
                          defaultValue={1}
                          render={({ field }) => (
                            <Dropdown
                              placeholder="Add"
                              name={field.name}
                              id={field.name}
                              className="w-100"
                              value={field.value}
                              onChange={(e) => field.onChange(e.value)}
                              options={reminderCountOptions}
                              filter
                            />
                          )}
                        />

                        <Controller
                          name={`reminder.${index}.type`}
                          control={control}
                          defaultValue="minutes"
                          render={({ field }) => (
                            <Dropdown
                              placeholder="Select Type"
                              name={field.name}
                              id={field.name}
                              value={field.value}
                              onChange={(e) => field.onChange(e.value)}
                              options={reminderCustomOptions}
                              filter
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div
                  onClick={(e) => {
                    onDeleteReminder(index);
                  }}
                  className="pointer"
                  style={{ marginLeft: '0.5rem' }}
                >
                  <i className="pi icon-delete"></i>
                  <Tooltip content="Delete Reminder" position="top" target=".icon-delete" showDelay={500} />
                </div>
              </div>
            );
          })}
        <div className="mt-2">
          <span className="pointer call-back" onClick={onAddReminder}>
            + Add Reminder
          </span>
        </div>
      </div>
      <div className="offset-md-4">
        {errors?.reminder?.root?.message && <InputErrorMessage>{errors.reminder.root.message}</InputErrorMessage>}
      </div>
    </div>
  );
}

export default EventReminders;
